import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import ChevronLeftIcon from "../../../assets/random/chevronLeft.svg";
import { useIntl } from "react-intl";

import { getListV2 } from "../../../services/api/Api";
import { StarRating } from "./StarRating/StarRating";

import { motion, AnimatePresence } from "framer-motion";

import "./BusinessCard.less";
import { OpinionResponse, OpinionsTab } from "./Tabs/OpinionsTab";
import { InfoTab } from "./Tabs/InfoTab";
import { PreviewTab } from "./Tabs/PreviewTab";
import { getLabelFromTechName } from "../../Newsletter/NewsletterSteps/ThirdStep";
import { Spin } from "../../BP2Components";
import { getDataFromFormTabs } from "../../../util/getDataFromFormTabs";

type BusinessCardTab = "Opinions" | "Info" | "Preview";

interface BusinessCardProps {
   tabs: {
      elements: Record<string, any>;
      id: string;
      name: string;
   }[];
}

const BusinessCard = ({ tabs }: BusinessCardProps) => {
   const [currentTab, setCurrentTab] = useState<BusinessCardTab>("Preview");
   const [files, setFiles] = useState<string[]>([]);
   const [rates, setRates] = useState<number[]>([]);
   const [opinions, setOpinions] = useState<OpinionResponse>(null);
   const [isLoading, setIsLoading] = useState<boolean>(true);
   const intl = useIntl();

   const businessCardTabs: BusinessCardTab[] = ["Preview", "Opinions"];

   const averageRate = rates?.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;

   const businessCardData = getDataFromFormTabs(tabs);

   const cardRef = useRef<HTMLDivElement>(null);

   const handleClickBackButton = () => setCurrentTab("Preview");

   const loadOpinions = async () => {
      try {
         if (!businessCardData.readonly_rfv3of || !businessCardData.kli_id_list) return;

         const opinionsRes = await getListV2(businessCardData.readonly_rfv3of, 1, 100, {
            Ratings_rated_object_id_fqvjx8: businessCardData.kli_id_list,
         });

         const { columns: opinionColumns, data: opinionsData } = opinionsRes.data.default;

         const rates = opinionsData
            .map((opinion) =>
               Number(
                  opinion[getLabelFromTechName("Ratings_rating_g60fqz", opinionColumns)]?.value,
               ),
            )
            .filter((rate) => !isNaN(rate));

         setOpinions(opinionsRes);
         setRates(rates);
      } catch (error) {
         console.error("Error loading opinions:", error);
      }
   };

   const loadFiles = async () => {
      try {
         if (!businessCardData.readonly_7pne3d || !businessCardData.kli_id_list) return;

         const filesRes = await getListV2(businessCardData.readonly_7pne3d, 1, 20, {
            top_id: businessCardData.kli_id_list,
         });

         const { columns: filesColumns, data: imagesData } = filesRes.data.default;

         const imagesAndVideos = imagesData
            .map((image) => {
               const imageData = image[getLabelFromTechName("Pages_banner_qxqm01", filesColumns)];
               return imageData && imageData[0] ? imageData[0].fileUrl : null;
            })
            .filter(Boolean);

         setFiles(imagesAndVideos);
      } catch (error) {
         console.error("Error loading images:", error);
      }
   };

   const loadData = async () => {
      setIsLoading(true);
      try {
         await loadOpinions();
         await loadFiles();
      } catch (err) {
         console.error("Error loading business card data:", err);
      } finally {
         setIsLoading(false);
      }
   };

   useEffect(() => {
      loadData();
   }, []);

   if (isLoading) {
      return (
         <div className="business-card business-card--loading">
            <Spin size="large" />
         </div>
      );
   }

   return (
      <div className="business-card" ref={cardRef}>
         {currentTab === "Preview" && businessCardData?.zdjecie ? (
            <img
               src={businessCardData.zdjecie}
               alt="preview image for business card"
               className="business-card__image"
            />
         ) : null}

         <div className="business-card__content-container">
            {currentTab === "Preview" && (
               <h2 className="business-card__subtitle">{businessCardData?.top_typ}</h2>
            )}

            <h1
               className={clsx(
                  "business-card__title",
                  currentTab !== "Preview" && "business-card__title--back-button",
               )}
            >
               {currentTab !== "Preview" && (
                  <button
                     type="button"
                     className="business-card__title-icon-button"
                     onClick={handleClickBackButton}
                  >
                     <img src={ChevronLeftIcon} alt="back to preview section button" />
                  </button>
               )}
               <span>{businessCardData?.nazwa}</span>
            </h1>

            {currentTab === "Preview" && (
               <StarRating rate={averageRate} numberOfOpinions={rates.length} />
            )}

            <ul className="business-card__tabs">
               {businessCardTabs.map((tab) => (
                  <li
                     key={tab}
                     className={clsx(
                        "business-card__category-button",
                        currentTab === tab && "business-card__category-button--active",
                     )}
                     onClick={() => {
                        setCurrentTab(tab);
                        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                     }}
                  >
                     {intl.formatMessage({ id: `business.${tab.toLowerCase()}` })}
                  </li>
               ))}
            </ul>

            <AnimatePresence initial={false}>
               <motion.div
                  key={currentTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
               >
                  {currentTab === "Preview" && (
                     <PreviewTab businessCardData={businessCardData} files={files} />
                  )}
                  {currentTab === "Opinions" && (
                     <OpinionsTab
                        opinionResponse={opinions}
                        rates={rates}
                        averageRate={averageRate}
                        kli_id_list={businessCardData.kli_id_list}
                        loadOpinions={loadOpinions}
                     />
                  )}
                  {currentTab === "Info" && <InfoTab />}
               </motion.div>
            </AnimatePresence>
         </div>
      </div>
   );
};

export default BusinessCard;
